import { useState, useEffect, useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getCmdbObjectConnectedTicketsPaginatedUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/object-connected-tickets.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import type { CmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { NUMBER_OF_OBJECT_PER_PAGE } from '../../common/constants.tsx';
import type {
	ObjectConnectedIssuesPaginated,
	ObjectConnectedIssuesInputs,
	Response,
} from './types.tsx';

const calculatePages = (data?: Response): number[] | undefined => {
	let pages;
	if (data && data.total > 0) {
		pages = Array.from(
			{ length: Math.floor((data.total - 1) / NUMBER_OF_OBJECT_PER_PAGE) + 1 },
			(_, i) => i + 1,
		);
	}
	return pages;
};

export const useObjectConnectedIssuesPaginated = (
	objectId?: CmdbObjectId,
	params?: {
		filterId?: string;
		textFilter?: string;
		hideResolved?: boolean;
	},
): ObjectConnectedIssuesPaginated => {
	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const { workspaceId } = useWorkspaceContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const request = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'startAt' implicitly has an 'any' type.
		({ startAt }) => {
			if (!objectId) {
				throw new Error('objectId undefined');
			}
			if (!workspaceId) {
				throw new Error('workspaceId undefined');
			}

			return performGetRequest(
				getCmdbObjectConnectedTicketsPaginatedUrl(workspaceId, objectId, {
					startAt,
					limit: NUMBER_OF_OBJECT_PER_PAGE,
					...(params?.filterId && { filterId: params.filterId }),
					...(params?.textFilter && { textFilter: params.textFilter }),
					hideResolved: params?.hideResolved,
				}),
			)
				.then((res: Response) => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectConnectedIssues succeeded');
					return res;
				})
				.catch((error) => {
					fireCmdbErrorAnalytics({
						error,
						meta: {
							id: 'fetchObjectConnectedIssues',
							packageName: 'jiraServicedeskInsightObjectServices',
							teamName: 'falcons',
						},
					});
					throw error;
				});
		},
		[
			createAnalyticsEvent,
			objectId,
			workspaceId,
			params?.filterId,
			params?.textFilter,
			params?.hideResolved,
		],
	);

	const { loading, error, data, fetch } = useService<Response, ObjectConnectedIssuesInputs>(
		request,
	);

	// Auto fetch connected issues when hook is called
	useEffect(() => {
		if (workspaceId) {
			fetch({ startAt: 0 });
		}
	}, [fetch, objectId, workspaceId]);

	const onPageChange = (pageNumber: number) => {
		const requestedPage = pageNumber >= 1 ? pageNumber : 1;

		setSelectedIndex(requestedPage - 1);
		fetch({
			startAt: (requestedPage - 1) * NUMBER_OF_OBJECT_PER_PAGE,
		});
	};

	return [
		{
			loading,
			error,
			jqlString: data?.allTicketsQuery,
			linkedIssues: data?.values,
			pages: calculatePages(data),
			pageIndex: selectedIndex,
		},
		{ onPageChange },
	];
};
